// ============= Imports =============
import React, { useState, useEffect } from 'react';
import { Modal, Headers, Spacer } from '@tw/tw-component-library';
import packageJson from '../../../../package.json';
import {
  VersionModalContent,
  VersionSection,
  VersionHeader,
  ChangeList,
  ChangeItem,
  ChangeCategory,
} from './VersionModal.styles';

// ============= Types =============
interface VersionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface VersionData {
  version: string;
  date: string;
  changes: {
    added: string[];
    changed: string[];
    fixed: string[];
    deprecated: string[];
    removed: string[];
    security: string[];
    other: string[];
  };
}

// ============= Component =============
export const DynamicVersionModal: React.FC<VersionModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [versionData, setVersionData] = useState<VersionData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const currentVersion = packageJson.version;

  const formatDateToISO = (dateString: string): string => {
    // Convert from DD-MM-YYYY to ISO format (YYYY-MM-DD) for internal use
    if (dateString.includes('-') && dateString.length === 10) {
      const parts = dateString.split('-');
      if (
        parts.length === 3 &&
        parts[0].length === 2 &&
        parts[1].length === 2 &&
        parts[2].length === 4
      ) {
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
    }
    return dateString; // Already in ISO format or invalid format
  };

  const formatDateForDisplay = (dateString: string): string => {
    // Convert from DD-MM-YYYY to a more readable format like "29 Jan 2025"
    if (dateString.includes('-') && dateString.length === 10) {
      const parts = dateString.split('-');
      if (
        parts.length === 3 &&
        parts[0].length === 2 &&
        parts[1].length === 2 &&
        parts[2].length === 4
      ) {
        const day = parseInt(parts[0]);
        const month = parseInt(parts[1]);
        const year = parseInt(parts[2]);

        const monthNames = [
          'Jan',
          'Feb',
          'Mar',
          'Apr',
          'May',
          'Jun',
          'Jul',
          'Aug',
          'Sep',
          'Oct',
          'Nov',
          'Dec',
        ];

        return `${day} ${monthNames[month - 1]} ${year}`;
      }
    }
    return dateString; // Already in readable format or invalid format
  };

  const loadVersionData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load from app root (public/CHANGELOG.md in dev, dist root in production)
      const response = await fetch('/CHANGELOG.md');
      if (response.ok) {
        const changelogContent = await response.text();
        if (changelogContent.trim()) {
          const parsedData = parseChangelogToVersionData(changelogContent);
          if (parsedData.length > 0 && hasValidChanges(parsedData)) {
            setVersionData(parsedData);
          } else {
            console.warn(
              'CHANGELOG.md has no valid version entries with changes, using fallback data'
            );
            setVersionData(getFallbackVersionData());
          }
        } else {
          console.warn('CHANGELOG.md is empty, using fallback data');
          setVersionData(getFallbackVersionData());
        }
      } else {
        // Fallback to static data if changelog not available
        console.warn('CHANGELOG.md not found, using fallback data');
        setVersionData(getFallbackVersionData());
      }
    } catch (err) {
      console.warn(
        'Failed to load dynamic changelog, using fallback data:',
        err
      );
      setVersionData(getFallbackVersionData());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadVersionData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const parseChangelogToVersionData = (
    changelogContent: string
  ): VersionData[] => {
    const lines = changelogContent.split('\n');
    const versions: VersionData[] = [];
    let currentVersion: VersionData | null = null;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      // Check if this is a version header (skip example sections and unreleased)
      if (
        line.match(/^## \[.*\]/) &&
        !line.includes('Example Entry Format') &&
        !line.includes('[Unreleased]')
      ) {
        // Save previous version if exists
        if (currentVersion) {
          versions.push(currentVersion);
        }

        const versionMatch = line.match(/^###? \[(.*?)\]/);
        if (versionMatch) {
          const version = versionMatch[1];
          // Handle both DD-MM-YYYY and YYYY-MM-DD formats
          const dateMatch1 = line.match(/- (\d{2}-\d{2}-\d{4})/); // UK format
          const dateMatch2 = line.match(/\(\d{4}-\d{2}-\d{2}\)/); // ISO format

          let date = '';
          if (dateMatch1) {
            date = dateMatch1[1]; // UK format
          } else if (dateMatch2) {
            // Convert ISO to UK format
            const isoDate = dateMatch2[0].replace(/[()]/g, '');
            const parts = isoDate.split('-');
            if (parts.length === 3) {
              date = `${parts[2]}-${parts[1]}-${parts[0]}`; // Convert to DD-MM-YYYY
            }
          }

          if (!date) {
            date = formatDateToISO(new Date().toISOString().split('T')[0]);
          }

          currentVersion = {
            version,
            date,
            changes: {
              added: [],
              changed: [],
              fixed: [],
              deprecated: [],
              removed: [],
              security: [],
              other: [],
            },
          };
        }
      }

      // Parse change categories (only if we're in a version section and not in example sections)
      if (
        currentVersion &&
        !line.includes('```') &&
        !line.includes('Example')
      ) {
        if (
          line.trim().startsWith('### Added') ||
          line.trim().startsWith('### Fixed') ||
          line.trim().startsWith('### Changed') ||
          line.trim().startsWith('### Deprecated') ||
          line.trim().startsWith('### Removed') ||
          line.trim().startsWith('### Security') ||
          line.trim().startsWith('### Other')
        ) {
          const category = line.trim().replace('### ', '').toLowerCase();
          switch (category) {
            case 'added':
              currentVersion.changes.added = extractChangeItems(lines, i);
              break;
            case 'changed':
              currentVersion.changes.changed = extractChangeItems(lines, i);
              break;
            case 'fixed':
              currentVersion.changes.fixed = extractChangeItems(lines, i);
              break;
            case 'deprecated':
              currentVersion.changes.deprecated = extractChangeItems(lines, i);
              break;
            case 'removed':
              currentVersion.changes.removed = extractChangeItems(lines, i);
              break;
            case 'security':
              currentVersion.changes.security = extractChangeItems(lines, i);
              break;
            case 'other':
              currentVersion.changes.other = extractChangeItems(lines, i);
              break;
          }
        }
      }
    }

    // Add the last version
    if (currentVersion) {
      versions.push(currentVersion);
    }

    return versions;
  };

  const extractChangeItems = (
    lines: string[],
    startIndex: number
  ): string[] => {
    const items: string[] = [];
    for (let i = startIndex + 1; i < lines.length; i++) {
      const line = lines[i];
      if (line.startsWith('###') || line.startsWith('##')) {
        break; // Next section
      }
      if (line.startsWith('- ')) {
        items.push(line.substring(2).trim());
      }
    }
    return items;
  };

  const hasValidChanges = (versions: VersionData[]): boolean => {
    return versions.some((version) => {
      // Consider a version valid if it has any change categories defined (even if empty)
      // or if it has actual changes in any category
      const changes = version.changes;
      const hasCategories =
        changes.added !== undefined ||
        changes.changed !== undefined ||
        changes.fixed !== undefined ||
        changes.deprecated !== undefined ||
        changes.removed !== undefined ||
        changes.security !== undefined ||
        changes.other !== undefined;

      const hasActualChanges =
        changes.added.length > 0 ||
        changes.changed.length > 0 ||
        changes.fixed.length > 0 ||
        changes.deprecated.length > 0 ||
        changes.removed.length > 0 ||
        changes.security.length > 0 ||
        changes.other.length > 0;

      return hasCategories || hasActualChanges;
    });
  };

  const getFallbackVersionData = (): VersionData[] => {
    return [
      {
        version: currentVersion,
        date: new Date().toISOString().split('T')[0],
        changes: {
          added: [
            'Dynamic version modal system',
            'Automated changelog generation',
            'Conventional commit enforcement',
            'Enhanced commit helper with multiple changes',
          ],
          changed: [
            'Updated version management workflow',
            'Enhanced commit message validation',
            'Improved version modal fallback system',
          ],
          fixed: [
            'Improved version display consistency',
            'Fixed React Hook dependency warnings',
          ],
          deprecated: [],
          removed: [],
          security: [],
          other: [],
        },
      },
    ];
  };

  const renderChangeCategory = (category: string, items: string[]) => {
    if (!items || items.length === 0) return null;

    const categoryLabels: Record<string, string> = {
      added: '✨ Added',
      changed: '🔄 Changed',
      fixed: '🐛 Fixed',
      deprecated: '⚠️ Deprecated',
      removed: '🗑️ Removed',
      security: '🔒 Security',
      other: '📌 Other',
    };

    return (
      <ChangeCategory key={category}>
        <strong>{categoryLabels[category] || category}</strong>
        <ChangeList>
          {items.map((item, index) => (
            <ChangeItem key={index}>{item}</ChangeItem>
          ))}
        </ChangeList>
      </ChangeCategory>
    );
  };

  if (loading) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={`Version History - v${currentVersion}`}
        size="large"
        height="600px"
      >
        <VersionModalContent>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p>Loading version history...</p>
          </div>
        </VersionModalContent>
      </Modal>
    );
  }

  if (error) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={`Version History - v${currentVersion}`}
        size="large"
        height="600px"
      >
        <VersionModalContent>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p>⚠️ Unable to load version history</p>
            <p>Using fallback data</p>
            <button
              onClick={loadVersionData}
              style={{
                marginTop: '1rem',
                padding: '0.5rem 1rem',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              Retry
            </button>
          </div>
        </VersionModalContent>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Version History - v${currentVersion}`}
      size="large"
      height="600px"
    >
      <VersionModalContent>
        <VersionHeader>
          <Headers headerType="h3">What's New</Headers>
          <p>
            Here's what's changed in recent versions of {packageJson.name}.
          </p>
        </VersionHeader>

        <Spacer />

        {versionData.map((version) => (
          <VersionSection key={version.version}>
            <Headers headerType="h3">
              v{version.version} - {formatDateForDisplay(version.date)}
            </Headers>

            {Object.entries(version.changes).map(([category, items]) =>
              renderChangeCategory(category, items)
            )}
          </VersionSection>
        ))}

        <Spacer />

        <VersionSection>
          <Headers headerType="h3">Technical Details</Headers>
          <ChangeList>
            <ChangeItem>React 19 with TypeScript</ChangeItem>
            <ChangeItem>Styled Components for styling</ChangeItem>
            <ChangeItem>Vite for build tooling</ChangeItem>
            <ChangeItem>ESLint for code quality</ChangeItem>
            <ChangeItem>Azure Artifacts for package management</ChangeItem>
            <ChangeItem>Yarn for dependency management</ChangeItem>
            <ChangeItem>Automated changelog generation</ChangeItem>
            <ChangeItem>Conventional commit enforcement</ChangeItem>
          </ChangeList>
        </VersionSection>
      </VersionModalContent>
    </Modal>
  );
};

// ============= PropTypes =============
DynamicVersionModal.displayName = 'DynamicVersionModal';
